import { css } from "@linaria/core";
import type { RefObject } from "react";
import { useEffect } from "react";
import { makeSelectors } from "../css/maker.selectors";
import { makeLengthToken } from "../css/token.length";
import { combineTokens } from "../css/util.combine-tokens";
import { throttleToAnimationFrame } from "../utils/dom.throttle-to-animation-frame";
import { isPlatformNodeJS } from "../utils/platform.is-node";


/**
 * Exports a collection of commonly used units, which should be favored over
 * using regular units (e.g. ${units.px(10)} instead of 10px).
 *
 * The reason for favoring these units is two-fold:
 *
 * 1) keeping units as CSS Variables allow hot-swapping values per-brand
 *    (e.g. one brand may need components rendered 10% larger which can easily
 *          be achieved by units.override() and setting the "px" to be
 *          slightly larger.)
 *
 * 2) As rem is still generally favored over px in order to respect user zoom
 *    etc. then unit.px() can be configured to use rem under the hood while
 *    developers can implement styling using the familiar px unit.
 */
const baseUnits = combineTokens("--unit", {
  px: ["1px", makeLengthToken],
  vh: ["1vh", makeLengthToken],
  vw: ["1vw", makeLengthToken],

  /**
   * resolves to the currently visible viewport height. In contrast to the
   * regular vh() unit, this unit takes the height of virtual keyboards etc.
   * into account.
   */
  vvh: ["1vh", makeLengthToken],
});

export const units = Object.assign(baseUnits, {
  font: { px: makeLengthToken("--unit--font-px", baseUnits.px(1)) },
});

// eslint-disable-next-line @typescript-eslint/no-unused-expressions
css`
  :global(:root) {
    @supports (height: 100dvh) {
      ${units.vh.__override("1dvh").toString()};
    }

    /**
     * designs for print are supplied for A4 pages that have assumed size
     * 287mmx210mm. However, this may not actually be the case when
     * printing in browsers.
     *
     * As such we override the default px unit to match what we expect a single
     * px to be in a printing context.
     *
     * In pixels this corresponds to 852x595px, which matches the pixel
     * measurements provided in designs.
     */
    @media print {
      ${units.px.__override(`${(1 / 287) * 100}mm`).toString()};
      ${units.vh.__override(units.px(842 / 100)).toString()};
      ${units.vw.__override(units.px(595 / 100)).toString()};
    }
  }
`;

export const virtualKeyboardOpened = makeSelectors("virtual-keyboard-visible");

/**
 * applies a --vh: [px] unit, which is automatically calculated based on the
 * window.innerHeight prop.
 *
 * we do this, because most mobile devices implement vh unit based on the false
 * assumption that their own UI shouldn't be included. the result is often that
 * layers which we expect to fit within the visible viewport slightly exceeds
 * it causing annoyance for the user.
 */
export function useVhUnit(): void {
  useEffect(() => {
    const supportsDVH = CSS.supports("height: 1dvh");

    if (supportsDVH) {
      return;
    }

    const updateVhUnit = (): void => {
      document.body.style.setProperty(
        units.vh.propName,
        units.px(window.innerHeight / 100),
      );

      document.body.style.setProperty(
        units.vvh.propName,
        units.px((window.visualViewport?.height ?? window.innerHeight) / 100),
      );

      if (window.visualViewport) {
        document.body.classList.toggle(
          virtualKeyboardOpened.className,
          window.visualViewport.height < window.innerHeight,
        );
      }
    };

    updateVhUnit();

    const onResize = throttleToAnimationFrame(updateVhUnit);

    window.visualViewport?.addEventListener("rezize", onResize);
    window.addEventListener("resize", onResize);
    window.addEventListener("orientationchange", onResize);

    return (): void => {
      window.visualViewport?.addEventListener("rezize", onResize);
      window.removeEventListener("resize", onResize);
      window.removeEventListener("orientationchange", onResize);
    };
  }, []);
}

const cachedElements = new WeakMap<HTMLElement, HTMLElement>();

/**
 * simply utility that allows extracting a JavaScript pixel number from a given
 * css token unit value
 *
 * @example getUnitValue(units.px(100)) -> 100
 */
export function getUnitValue(
  rawUnit: string,
  root: HTMLElement | RefObject<HTMLElement | null> = document.body,
  strategy?: "clientWidth" | "boundingBox",
) {
  if (isPlatformNodeJS()) {
    return 0;
  }

  const resolvedRoot =
    "current" in root ? (root.current ?? document.body) : root;

  let cachedElement = cachedElements.get(resolvedRoot);

  if (!cachedElement) {
    cachedElement = document.createElement("div");
    cachedElement.style.cssText = `
      position: absolute;
      top: auto;
      right: -10000px;
      opacity: 0;
    `;

    resolvedRoot.appendChild(cachedElement);
  }

  cachedElement.style.width = rawUnit;
  const computedValue =
    strategy === "clientWidth"
      ? cachedElement.clientWidth
      : cachedElement.getBoundingClientRect().width;

  return computedValue;
}
